/**
 * Stock Level Utilities
 * Helper functions for stock level visualization and management
 */

/**
 * Get background and text color style based on stock level and thresholds
 * @param {number} stockLevel - Current stock level in Kg
 * @param {object} thresholds - Object containing threshold values
 * @param {string} thresholds.stock_threshold_critical - Critical level (red)
 * @param {string} thresholds.stock_threshold_low - Low level (yellow)
 * @param {string} thresholds.stock_threshold_medium - Medium level (green)
 * @returns {object} Style object with backgroundColor and color
 */
export const getStockLevelStyle = (stockLevel, thresholds) => {
  if (!thresholds) return {};
  
  // Check if color indicators are enabled
  const colorsEnabled = thresholds.color_indicators_enabled === 'true' || thresholds.color_indicators_enabled === true;
  
  // If disabled, return just black text
  if (!colorsEnabled) {
    return {
      color: '#000000',
      fontWeight: 'normal'
    };
  }
  
  const critical = parseFloat(thresholds.stock_threshold_critical || 50);
  const low = parseFloat(thresholds.stock_threshold_low || 100);
  const medium = parseFloat(thresholds.stock_threshold_medium || 200);
  
  const stock = parseFloat(stockLevel);
  
  // Negative or zero stock - Red cell with white text
  if (stock <= 0) {
    return { 
      backgroundColor: '#b84a4a', 
      color: '#ffffff',
      fontWeight: 'bold'
    };
  }
  
  // Critical level (Red) - Red cell with white text
  if (stock <= critical) {
    return { 
      backgroundColor: '#b84a4a', 
      color: '#ffffff',
      fontWeight: 'bold'
    };
  }
  
  // Low level (Orange) - Orange cell with white text
  if (stock <= low) {
    return { 
      backgroundColor: '#ff8800',
      color: '#ffffff',
      fontWeight: 'bold'
    };
  }
  
  // Medium level (Light Green) - Light green cell with white text
  if (stock <= medium) {
    return { 
      backgroundColor: '#4f8d6a',
      color: '#ffffff',
      fontWeight: 'bold'
    };
  }
  
  // High level (Dark Green) - Dark green cell with white text
  return { 
    backgroundColor: '#2f5e44',
    color: '#ffffff',
    fontWeight: 'bold'
  };
};

/**
 * Get emoji indicator for stock level
 * @param {number} stockLevel - Current stock level in Kg
 * @param {object} thresholds - Object containing threshold values
 * @returns {string} Emoji indicator
 */
export const getStockLevelIndicator = (stockLevel, thresholds) => {
  if (!thresholds) return '';
  
  const critical = parseFloat(thresholds.stock_threshold_critical || 50);
  const low = parseFloat(thresholds.stock_threshold_low || 100);
  const medium = parseFloat(thresholds.stock_threshold_medium || 200);
  
  const stock = parseFloat(stockLevel);
  
  if (stock <= 0) return '⚫'; // Out of stock
  if (stock <= critical) return '🔴'; // Critical
  if (stock <= low) return '🟡'; // Low
  if (stock <= medium) return '🟢'; // Medium
  return '🟢'; // High (bright green)
};

/**
 * Get stock level label
 * @param {number} stockLevel - Current stock level in Kg
 * @param {object} thresholds - Object containing threshold values
 * @returns {string} Label text
 */
export const getStockLevelLabel = (stockLevel, thresholds) => {
  if (!thresholds) return 'Unknown';
  
  const critical = parseFloat(thresholds.stock_threshold_critical || 50);
  const low = parseFloat(thresholds.stock_threshold_low || 100);
  const medium = parseFloat(thresholds.stock_threshold_medium || 200);
  
  const stock = parseFloat(stockLevel);
  
  if (stock <= 0) return 'Out of Stock';
  if (stock <= critical) return 'Critical';
  if (stock <= low) return 'Low';
  if (stock <= medium) return 'Good';
  return 'Excellent';
};

/**
 * Count stock items by level
 * @param {array} stockItems - Array of stock items
 * @param {object} thresholds - Object containing threshold values
 * @returns {object} Count of items by level
 */
export const getStockLevelCounts = (stockItems, thresholds) => {
  const counts = {
    outOfStock: 0,
    critical: 0,
    low: 0,
    medium: 0,
    high: 0,
    total: stockItems.length
  };
  
  if (!thresholds) return counts;
  
  const critical = parseFloat(thresholds.stock_threshold_critical || 50);
  const low = parseFloat(thresholds.stock_threshold_low || 100);
  const medium = parseFloat(thresholds.stock_threshold_medium || 200);
  
  stockItems.forEach(item => {
    const stock = parseFloat(item.current_stock);
    
    if (stock <= 0) counts.outOfStock++;
    else if (stock <= critical) counts.critical++;
    else if (stock <= low) counts.low++;
    else if (stock <= medium) counts.medium++;
    else counts.high++;
  });
  
  return counts;
};
